import { useCallback } from "react";
import useNativeApp from "../../../../hooks/useNativeApp";
import { getAppUpdate } from "../../../../api/app-update";
import { openNewWindow } from "../../../../utils/common";
import useDialog from "../../../../hooks/useDialog";

const useAppUpdate = () => {
  const { sendMessageToNative } = useNativeApp();
  const { openDialog } = useDialog();
  const compareVersions = useCallback(
    (currentVersion: string, latestVersion: string): boolean => {
      const versionTuple = (version: string): number[] => {
        return version.split(".").map((num) => parseInt(num, 10));
      };

      const currentVersionTuple = versionTuple(currentVersion);
      const latestVersionTuple = versionTuple(latestVersion);

      for (
        let i = 0;
        i < Math.max(currentVersionTuple.length, latestVersionTuple.length);
        i++
      ) {
        const current = currentVersionTuple[i] || 0;
        const latest = latestVersionTuple[i] || 0;

        if (current < latest) {
          return true;
        } else if (current > latest) {
          return false;
        }
      }

      return false; // Versions are the same
    },
    []
  );

  const checkAppUpdate = useCallback(() => {
    sendMessageToNative("basicInfo", "basicInfoCallback", {}).then(
      async (res: any) => {
        if (!res) {
          return;
        }

        const updateData = await getAppUpdate();
        const androidRequired = updateData.find((e) => e.os === "android");
        const iosRequired = updateData.find((e) => e.os === "ios");

        if (res.os === "android" && androidRequired) {
          const valid = compareVersions(res.version, androidRequired?.version);
          if (valid) {
            openDialog({
              title: "앱 업데이트",
              text: "최신 버전으로 업데이트를 해주세요",
              confirm: !androidRequired.update,
              confirmText: "업데이트",
              type: "app-update",
              disableAutoClose: androidRequired.update,
              onConfirm: () => {
                const url = `https://play.google.com/store/apps/details?id=${androidRequired.appId}`;
                openNewWindow(url);
              },
            });
          }
        } else {
          if (!iosRequired) {
            return;
          }
          const valid = compareVersions(res.version, iosRequired.version);

          if (valid) {
            openDialog({
              title: "앱 업데이트",
              text: "최신 버전으로 업데이트를 해주세요",
              confirm: !iosRequired.update,
              confirmText: "업데이트",
              type: "app-update",
              disableAutoClose: iosRequired.update,
              onConfirm: () => {
                const url = `https://apps.apple.com/app/${iosRequired.appId}`;
                openNewWindow(url);
              },
            });
          }
        }
      }
    );
  }, [sendMessageToNative, compareVersions]);

  return {
    checkAppUpdate,
    compareVersions,
  };
};

export default useAppUpdate;
